import { useList, useCustomMutation, useGo } from '@refinedev/core';
import { useQueryClient } from '@tanstack/react-query';
import { Plus, Ticket } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { PricingTiersList } from '@/components/shared/PricingTiersList';
import {
  DataTable,
  TableToolbar,
  FilterSelect,
  type Column,
  type SortState,
  type BulkAction,
} from '@/components/tables';
import { FormHeader } from '@/components/forms';
import { notify } from '@/lib/notify';
import { resolveApiUrl } from '@/providers/api-url';
import {
  type ChannelPass,
  getChannelPassName,
} from './types';
import { usePersistedState } from '@/hooks/use-persisted-state';
import { useScrollRestoration } from '@/hooks/use-scroll-restoration';

const API_URL = resolveApiUrl();

interface TelegramChannelOption {
  id: number;
  title: string;
  username: string | null;
}

export function ChannelPassesList() {
  const go = useGo();
  const queryClient = useQueryClient();
  useScrollRestoration();
  const [page, setPage] = usePersistedState('channel-passes:page', 1);
  const [sort, setSort] = usePersistedState<SortState | undefined>('channel-passes:sort', { field: 'createdAt', order: 'desc' });
  const [search, setSearch] = usePersistedState('channel-passes:search', '');
  const [statusFilter, setStatusFilter] = usePersistedState('channel-passes:statusFilter', 'all');
  const [channelFilter, setChannelFilter] = usePersistedState('channel-passes:channelFilter', 'all');
  const pageSize = 20;

  const { result, query } = useList<ChannelPass>({
    resource: 'channel-passes',
    pagination: { currentPage: page, pageSize, mode: 'server' },
    filters: [
      ...(search ? [{ field: 'search', operator: 'contains' as const, value: search }] : []),
      ...(statusFilter !== 'all'
        ? [{ field: 'isActive', operator: 'eq' as const, value: statusFilter === 'true' }]
        : []),
      ...(channelFilter !== 'all'
        ? [{ field: 'channelId', operator: 'eq' as const, value: Number(channelFilter) }]
        : []),
    ],
    sorters: sort ? [{ field: sort.field, order: sort.order }] : [],
  });

  const { result: channelsResult } = useList<TelegramChannelOption>({
    resource: 'telegram-channels',
    pagination: { pageSize: 200 },
  });

  const data = result?.data ?? [];
  const total = result?.total ?? 0;
  const isLoading = query.isLoading;
  const channels = channelsResult?.data ?? [];

  const { mutate: bulkMutate } = useCustomMutation();

  const handleBulkActivate = (items: ChannelPass[]) => {
    const ids = items.map((item) => item.id);
    bulkMutate(
      {
        url: `${API_URL}/channel-passes/admin/bulk/activate`,
        method: 'post',
        values: { ids },
      },
      {
        onSuccess: () => {
          notify.success(`Активировано: ${ids.length}`);
          queryClient.invalidateQueries({ queryKey: ['data', 'default', 'channel-passes'] });
        },
        onError: () => {
          notify.error('Ошибка при активации');
        },
      },
    );
  };

  const handleBulkDeactivate = (items: ChannelPass[]) => {
    const ids = items.map((item) => item.id);
    bulkMutate(
      {
        url: `${API_URL}/channel-passes/admin/bulk/deactivate`,
        method: 'post',
        values: { ids },
      },
      {
        onSuccess: () => {
          notify.success(`Деактивировано: ${ids.length}`);
          queryClient.invalidateQueries({ queryKey: ['data', 'default', 'channel-passes'] });
        },
        onError: () => {
          notify.error('Ошибка при деактивации');
        },
      },
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  
  const renderLimitCell = (pass: ChannelPass) => {
    if (!pass.userLimit) {
      return (
        <div className="flex items-center gap-1.5">
          <span className="text-muted-foreground">{pass.currentUserCount}</span>
          <span className="text-muted-foreground">/</span>
          <span className="text-muted-foreground text-lg leading-none">&infin;</span>
        </div>
      );
    }

    const percent = Math.min(Math.round((pass.currentUserCount / pass.userLimit) * 100), 100);

    return (
      <div className="space-y-1 min-w-[100px]">
        <div className="flex items-center justify-between text-sm">
          <span>{pass.currentUserCount}/{pass.userLimit}</span>
          <span className="text-muted-foreground text-xs">{percent}%</span>
        </div>
        <Progress value={percent} className="h-1.5" />
      </div>
    );
  };

  const columns: Column<ChannelPass>[] = [
    {
      key: 'channel',
      header: 'Канал',
      render: (_, pass) => {
        const channel = pass.channel;
        if (!channel) {
          return <span className="text-muted-foreground">—</span>;
        }
        return (
          <div>
            <div className="font-medium">{channel.title}</div>
            {channel.username && (
              <div className="text-xs text-muted-foreground">@{channel.username}</div>
            )}
          </div>
        );
      },
    },
    {
      key: 'name',
      header: 'Название',
      render: (_, pass) => {
        const name = getChannelPassName(pass);
        return <span className="font-medium">{name}</span>;
      },
    },
    {
      key: 'pricingTiers',
      header: 'Тарифы',
      render: (_, pass) => (
        <PricingTiersList
          tiers={pass.pricingTiers}
          onTierClick={(tier) => go({ to: `/channel-passes/show/${pass.id}`, query: { tab: 'pricing', tier: tier.id } })}
        />
      ),
    },
    {
      key: 'targetLanguages',
      header: 'Аудитория',
      render: (_, pass) => {
        if (pass.targetLanguages.length === 0) {
          return <span className="text-muted-foreground">Все</span>;
        }
        return (
          <div className="flex flex-wrap gap-1">
            {pass.targetLanguages.map((language) => (
              <Badge key={language} variant="outline" className="uppercase">
                {language}
              </Badge>
            ))}
          </div>
        );
      },
    },
    {
      key: 'durationDays',
      header: 'Срок',
      render: (_, pass) => {
        const tiers = pass.pricingTiers?.filter((t) => t.isActive) ?? [];
        if (tiers.length === 0) {
          return <span className="text-muted-foreground">—</span>;
        }

        const durations = [...new Set(tiers.map((t) => t.durationDays))];

        if (durations.length === 1) {
          const d = durations[0];
          return d === null ? 'Бессрочный' : `${d} дней`;
        }

        // Несколько разных сроков
        const formatted = durations.map((d) => (d === null ? 'Бессрочный' : `${d} дн.`));
        return <span className="text-sm">{formatted.join(', ')}</span>;
      },
    },
    {
      key: 'currentUserCount',
      header: 'Лимит',
      render: (_, pass) => renderLimitCell(pass),
    },
    {
      key: 'isActive',
      header: 'Статус',
      render: (_, pass) =>
        pass.isActive ? (
          <Badge className="bg-green-500/10 text-green-500 hover:bg-green-500/20">Активна</Badge>
        ) : (
          <Badge variant="secondary">Неактивна</Badge>
        ),
    },
    {
      key: 'createdAt',
      header: 'Создан',
      sortable: true,
      render: (value) => formatDate(String(value)),
    },
  ];

  const bulkActions: BulkAction<ChannelPass>[] = [
    {
      label: 'Активировать',
      onClick: handleBulkActivate,
      showOnlyWhenSelected: true,
    },
    {
      label: 'Деактивировать',
      onClick: handleBulkDeactivate,
      variant: 'destructive',
      showOnlyWhenSelected: true,
    },
  ];

  const filtersCount =
    (statusFilter !== 'all' ? 1 : 0) + (channelFilter !== 'all' ? 1 : 0);

  return (
    <div className="space-y-6">
      <FormHeader
        title="Подписки в канал"
        icon={<Ticket className="h-5 w-5" />}
        actions={
          <Button onClick={() => go({ to: '/channel-passes/create' })}>
            <Plus className="h-4 w-4 mr-2" />
            Создать подписку
          </Button>
        }
        sticky
      />

      <TableToolbar
        searchValue={search}
        onSearchChange={(v) => {
          setSearch(v);
          setPage(1);
        }}
        searchPlaceholder="Поиск по подпискам..."
        filters={
          <div className="grid gap-4 sm:grid-cols-2">
            <FilterSelect
              label="Статус"
              value={statusFilter}
              onChange={(v) => {
                setStatusFilter(v);
                setPage(1);
              }}
              options={[
                { value: 'true', label: 'Активные' },
                { value: 'false', label: 'Неактивные' },
              ]}
            />
            <FilterSelect
              label="Канал"
              value={channelFilter}
              onChange={(v) => {
                setChannelFilter(v);
                setPage(1);
              }}
              options={channels.map((ch) => ({
                value: String(ch.id),
                label: ch.title + (ch.username ? ` (@${ch.username})` : ''),
              }))}
            />
          </div>
        }
        filtersCount={filtersCount}
        onResetFilters={() => {
          setSearch('');
          setStatusFilter('all');
          setChannelFilter('all');
          setPage(1);
        }}
      />

      <DataTable
        columns={columns}
        data={data}
        total={total}
        page={page}
        pageSize={pageSize}
        isLoading={isLoading}
        onPageChange={setPage}
        onRowClick={(pass) => go({ to: `/channel-passes/show/${pass.id}` })}
        emptyMessage="Подписки не найдены"
        selectable
        rowKey="id"
        sort={sort}
        onSortChange={setSort}
        bulkActions={bulkActions}
      />
    </div>
  );
}
